import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { productosAPI, ventasAPI, utilAPI, fmt } from '../services/api'
import { useAuth } from '../context/AuthContext'
import Boleta from '../components/Boleta'
import EscanerCodigo from '../components/EscanerCodigo'
import toast from 'react-hot-toast'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'

export default function POS() {
  const { usuario } = useAuth()
  const navigate = useNavigate()
  const [productos, setProductos]   = useState([])
  const [carrito, setCarrito]       = useState([])
  const [busqueda, setBusqueda]     = useState('')
  const [medios, setMedios]         = useState([])
  const [pagoSel, setPagoSel]       = useState(null)
  // Tipo de documento a emitir: 'boleta' (va al SII) o 'nota_venta' (Ticket interno).
  // Vuelve a 'boleta' después de cada venta a propósito: así elegir Ticket es
  // siempre una decisión consciente y nunca queda "pegado" sin que nadie lo note.
  const [tipoDoc, setTipoDoc]       = useState('boleta')
  const [recibe, setRecibe]         = useState('')
  const [cargando, setCargando]     = useState(false)
  const [turnoId, setTurnoId]       = useState(localStorage.getItem('pos_turno'))
  const [ventaCompletada, setVentaCompletada] = useState(null)
  const [generandoPdf, setGenerandoPdf] = useState(false)
  const [carritoAbierto, setCarritoAbierto] = useState(false)
  const [escaneando, setEscaneando] = useState(false)
  const [scanResultado, setScanResultado] = useState(null)
  // Editor de cantidad/peso: { nombre, esKilo, valor, producto? (agregar) | id? (corregir) }
  const [editQty, setEditQty] = useState(null)
  const busquedaRef = useRef()

  const esKilo = p => p.unidad === 'kg' || p.unidad === 'kilo'

  useEffect(() => {
    cargarProductos()
    utilAPI.mediosPago().then(r => {
      setMedios(r.data)
      const ef = r.data.find(m => m.tipo === 'efectivo')
      if (ef) setPagoSel(ef)
    })
    busquedaRef.current?.focus()
  }, [])

  const cargarProductos = async (q = '') => {
    const { data } = await productosAPI.listar({ q, limit: 100 })
    setProductos(data.data)
  }

  const handleBusqueda = e => {
    setBusqueda(e.target.value)
    cargarProductos(e.target.value)
  }

  const agregar = prod => {
    // Productos que se pesan: preguntar los kilos antes de agregar
    if (esKilo(prod)) {
      setEditQty({ producto: prod, nombre: prod.nombre, esKilo: true, valor: '' })
      return
    }
    setCarrito(prev => {
      const ex = prev.find(i => i.id === prod.id)
      if (ex) return prev.map(i => i.id === prod.id ? {...i, qty: i.qty + 1} : i)
      return [...prev, { ...prod, qty: 1 }]
    })
  }

  // Abrir el editor tocando el recuadro de cantidad de una línea
  const corregirCantidad = item => {
    setEditQty({ id: item.id, nombre: item.nombre, esKilo: esKilo(item), valor: String(item.qty) })
  }

  const confirmarCantidad = () => {
    // Aceptar coma (uso chileno) o punto como separador decimal
    const valor = parseFloat(String(editQty.valor).replace(',', '.'))
    if (isNaN(valor) || valor < 0) { toast.error('Ingresa una cantidad válida'); return }
    const qty = editQty.esKilo ? Math.round(valor * 1000) / 1000 : Math.round(valor)

    if (editQty.producto) {
      // Agregar producto pesado (si ya estaba, se suma el nuevo pesaje)
      if (qty <= 0) { setEditQty(null); return }
      const prod = editQty.producto
      setCarrito(prev => {
        const ex = prev.find(i => i.id === prod.id)
        if (ex) return prev.map(i => i.id === prod.id ? {...i, qty: Math.round((i.qty + qty) * 1000) / 1000} : i)
        return [...prev, { ...prod, qty }]
      })
    } else {
      // Corregir la cantidad de una línea existente (0 la elimina)
      setCarrito(prev => prev
        .map(i => i.id === editQty.id ? {...i, qty} : i)
        .filter(i => i.qty > 0)
      )
    }
    setEditQty(null)
  }

  const cambiarQty = (id, delta) => {
    setCarrito(prev => prev
      .map(i => i.id === id ? {...i, qty: i.qty + delta} : i)
      .filter(i => i.qty > 0)
    )
  }

  const limpiar = () => { setCarrito([]); setRecibe(''); setTipoDoc('boleta') }

  const esTicket = tipoDoc === 'nota_venta'
  const nombreDoc = esTicket ? 'ticket' : 'boleta'

  // La cámara detectó un código de barras
  const codigoDetectado = async codigo => {
    try {
      const { data } = await productosAPI.listar({ q: codigo, limit: 5 })
      const encontrados = data.data
      // coincidencia exacta por código de barra tiene prioridad
      const exacto = encontrados.find(p => p.codigo_barra === codigo) || (encontrados.length === 1 ? encontrados[0] : null)

      if (exacto) {
        agregar(exacto)
        setScanResultado({ tipo: 'ok', texto: exacto.nombre, k: Date.now() })
      } else if (encontrados.length > 1) {
        // Varios coinciden: cerrar cámara y mostrar la búsqueda para elegir
        setEscaneando(false)
        setBusqueda(codigo)
        setProductos(encontrados)
      } else {
        setScanResultado({ tipo: 'error', texto: 'Código no registrado. Agrégalo en Productos.', k: Date.now() })
      }
    } catch {
      setScanResultado({ tipo: 'error', texto: 'No se pudo verificar el código. Intenta de nuevo.', k: Date.now() })
    }
  }

  const subtotal   = Math.round(carrito.reduce((s, i) => s + i.precio_bruto * i.qty, 0))
  const totalItems = carrito.reduce((s, i) => s + (esKilo(i) ? 1 : i.qty), 0)
  const neto       = Math.round(subtotal / 1.19)
  const iva        = subtotal - neto
  const recibeNum  = parseFloat(String(recibe).replace(',', '.')) || 0
  const vuelto     = pagoSel?.tipo === 'efectivo' ? Math.max(0, recibeNum - subtotal) : 0

  const cobrar = async () => {
    if (!carrito.length) { toast.error('Agrega productos al carrito'); return }
    if (!pagoSel)        { toast.error('Selecciona un medio de pago'); return }
    if (!turnoId)        { toast.error('Debes abrir la caja primero'); return }
    if (pagoSel.tipo === 'efectivo' && recibeNum < subtotal) {
      toast.error('El monto recibido es insuficiente'); return
    }
    setCargando(true)
    try {
      const itemsVenta = carrito.map(i => ({
        producto_id: i.id,
        descripcion: i.nombre,
        cantidad: i.qty,
        precio_unitario: i.precio_bruto,
      }))
      const payload = {
        turno_id: turnoId,
        tipo: tipoDoc,
        items: itemsVenta,
        pagos: [{ medio_pago_id: pagoSel.id, monto: subtotal }]
      }
      const { data } = await ventasAPI.crear(payload)

      // Guardar datos para el modal de boleta
      setVentaCompletada({
        documento: data.documento,
        items: itemsVenta,
        pago: pagoSel,
        vuelto: vuelto,
      })

      const numero = data.documento.folio || data.documento.id.slice(-6).toUpperCase()

      // Si OpenFactura rechazó la boleta, la venta SÍ quedó registrada y el stock
      // ya se descontó — pero hay que avisarlo, no mostrar un "éxito" engañoso.
      if (data.sii && data.sii.emitido === false) {
        toast.error(
          data.sii.mensaje || 'La venta se registró, pero la boleta no pudo emitirse ante el SII.',
          { duration: 8000 }
        )
      } else {
        toast.success(`${esTicket ? 'Ticket' : 'Boleta'} #${numero} emitido`)
      }
      limpiar()
      setCarritoAbierto(false)
      cargarProductos()
      busquedaRef.current?.focus()
    } finally { setCargando(false) }
  }

  const imprimir = () => {
    window.print()
  }

  const descargarPdf = async () => {
    setGenerandoPdf(true)
    try {
      const elemento = document.getElementById('boleta-imprimir')
      const canvas = await html2canvas(elemento, { scale: 3, backgroundColor: '#ffffff' })
      const imgData = canvas.toDataURL('image/png')

      // Ancho fijo 80mm, alto proporcional
      const anchoMm = 80
      const altoMm = (canvas.height * anchoMm) / canvas.width

      const pdf = new jsPDF({
        unit: 'mm',
        format: [anchoMm, altoMm],
      })
      pdf.addImage(imgData, 'PNG', 0, 0, anchoMm, altoMm)

      const folio = ventaCompletada?.documento?.folio || ventaCompletada?.documento?.id?.slice(-6).toUpperCase()
      const prefijo = ventaCompletada?.documento?.tipo === 'nota_venta' ? 'ticket' : 'boleta'
      pdf.save(`${prefijo}-${folio}.pdf`)
    } catch (err) {
      toast.error('Error al generar el PDF')
    } finally {
      setGenerandoPdf(false)
    }
  }

  const cerrarModalVenta = () => {
    setVentaCompletada(null)
  }

  // Ojo: el modal NO puede mirar tipoDoc, porque limpiar() ya lo devolvió a
  // 'boleta'. El tipo real de lo que se emitió viene en el documento guardado.
  const docEmitido = ventaCompletada?.documento
  const emitidoEsTicket = docEmitido?.tipo === 'nota_venta'
  const emitidoNombre = emitidoEsTicket ? 'Ticket' : 'Boleta'
  const emitidoNumero = docEmitido?.folio || docEmitido?.id?.slice(-6).toUpperCase()

  // ── Panel de cobro (se usa en el panel derecho de escritorio
  //    y dentro del carrito deslizable en móvil) ──
  const renderCobro = () => (
    <div className="cart-footer">
      <div className="cart-totals">
        <div className="cart-total-row"><span>Neto</span><span>{fmt(neto)}</span></div>
        <div className="cart-total-row"><span>IVA 19%</span><span>{fmt(iva)}</span></div>
        <div className="cart-total-row final"><span>Total</span><span>{fmt(subtotal)}</span></div>
      </div>

      <div style={{fontSize:12,fontWeight:500,color:'var(--color-muted)',marginBottom:6}}>Tipo de documento</div>
      <div className="pay-methods" style={{marginBottom:4}}>
        <button className={`pay-btn ${!esTicket ? 'active' : ''}`}
          onClick={() => setTipoDoc('boleta')}>
          🧾 Boleta electrónica
        </button>
        <button className={`pay-btn ${esTicket ? 'active' : ''}`}
          onClick={() => setTipoDoc('nota_venta')}>
          📄 Ticket detalle
        </button>
      </div>
      <div style={{fontSize:11,color:'var(--color-hint)',marginBottom:12}}>
        {esTicket
          ? 'Comprobante interno con el detalle. No va al SII.'
          : 'Documento tributario. Se emite ante el SII.'}
      </div>

      <div style={{fontSize:12,fontWeight:500,color:'var(--color-muted)',marginBottom:6}}>Medio de pago</div>
      <div className="pay-methods">
        {medios.map(m => (
          <button key={m.id} className={`pay-btn ${pagoSel?.id === m.id ? 'active' : ''}`}
            onClick={() => setPagoSel(m)}>
            {m.nombre}
          </button>
        ))}
      </div>

      {pagoSel?.tipo === 'efectivo' && (
        <div style={{marginBottom:12}}>
          <div className="form-group" style={{marginBottom:6}}>
            <label className="form-label">Recibe</label>
            <input className="form-input" type="text" inputMode="decimal" placeholder="$0"
              value={recibe} onChange={e => setRecibe(e.target.value.replace(/[^0-9.,]/g, ''))}/>
          </div>
          <div style={{display:'flex',justifyContent:'space-between',fontSize:13}}>
            <span style={{color:'var(--color-muted)'}}>Vuelto</span>
            <span style={{fontWeight:700,color:vuelto >= 0 ? 'var(--color-success)' : 'var(--color-danger)'}}>
              {fmt(vuelto)}
            </span>
          </div>
        </div>
      )}

      <button className="btn btn-primary btn-lg btn-block" onClick={cobrar}
        disabled={cargando || !carrito.length || !turnoId}>
        {cargando ? <span className="spinner"/> : `Cobrar ${fmt(subtotal)} y emitir ${nombreDoc}`}
      </button>
    </div>
  )

  return (
    <div className="pos-page">
      <div className="pos-v2">

        {/* Buscador superior (aquí también dispara el lector USB) */}
        <div className="pos-search" style={{display:'flex', gap:8}}>
          <input ref={busquedaRef} className="form-input" placeholder="🔍  Buscar producto o código de barra..."
            value={busqueda} onChange={handleBusqueda}
            style={{fontSize:16,padding:'11px 14px',flex:1}}/>
          {busqueda && (
            <button className="btn btn-secondary pos-listo" onClick={() => busquedaRef.current?.blur()}
              title="Cerrar teclado" style={{padding:'0 16px'}}>
              Listo
            </button>
          )}
          <button className="btn btn-secondary" onClick={() => setEscaneando(true)}
            title="Escanear con la cámara" style={{padding:'0 14px'}}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2"/>
              <line x1="7" y1="8" x2="7" y2="16"/><line x1="10.5" y1="8" x2="10.5" y2="16"/>
              <line x1="13.5" y1="8" x2="13.5" y2="16"/><line x1="17" y1="8" x2="17" y2="16"/>
            </svg>
          </button>
        </div>

        {!turnoId && (
          <div style={{
            background:'var(--color-warning-bg)', color:'var(--color-warning)', padding:'10px 16px',
            borderRadius:'var(--radius-sm)', fontSize:13, fontWeight:500,
            display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, flexWrap:'wrap',
          }}>
            <span>⚠️ Abre la caja antes de comenzar a vender</span>
            <button className="btn btn-primary" style={{padding:'6px 14px', fontSize:12, whiteSpace:'nowrap'}}
              onClick={() => navigate('/caja')}>
              Abrir caja →
            </button>
          </div>
        )}

        <div className="pos-main">

          {/* ── Ticket central: lo escaneado en GRANDE (solo escritorio) ── */}
          <div className="ticket-panel">
            <div className="ticket-header">
              <span>Carrito ({totalItems} items)</span>
              {carrito.length > 0 && (
                <button onClick={limpiar} style={{background:'none',border:'none',cursor:'pointer',color:'var(--color-muted)',fontSize:12}}>
                  Limpiar
                </button>
              )}
            </div>
            <div className="ticket-items">
              {carrito.length === 0 && (
                <div className="ticket-empty">
                  Escanea o toca un producto para comenzar la venta
                </div>
              )}
              {carrito.map(item => (
                <div key={item.id} className="ticket-item">
                  <button className="tk-qty" onClick={() => corregirCantidad(item)} title="Corregir cantidad">
                    {item.qty}{esKilo(item) && <span className="tk-kg"> kg</span>}
                  </button>
                  <div className="tk-name">{item.nombre}</div>
                  {esKilo(item) ? (
                    <div className="tk-controls">
                      <button onClick={() => corregirCantidad(item)} title="Pesar de nuevo">⚖</button>
                    </div>
                  ) : (
                    <div className="tk-controls">
                      <button onClick={() => cambiarQty(item.id, -1)}>−</button>
                      <span>{item.qty}</span>
                      <button onClick={() => cambiarQty(item.id, 1)}>+</button>
                    </div>
                  )}
                  <div className="tk-total">{fmt(item.precio_bruto * item.qty)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Columna derecha: catálogo compacto + cobro (escritorio) /
                 catálogo principal (móvil) ── */}
          <div className="pos-right">
            <div className="product-grid product-grid--mini">
              {productos.map(p => (
                <button key={p.id} className="product-card" onClick={() => { agregar(p); busquedaRef.current?.blur() }}>
                  <div className="pc-name">{p.nombre}</div>
                  <div className="pc-price">{fmt(p.precio_bruto)}</div>
                </button>
              ))}
              {productos.length === 0 && (
                <div style={{gridColumn:'1/-1',textAlign:'center',padding:'40px 0',color:'var(--color-muted)',fontSize:13}}>
                  No se encontraron productos
                </div>
              )}
            </div>

            <div className="cobro-panel">
              {renderCobro()}
            </div>
          </div>

        </div>
      </div>

      {/* ── Botón flotante del carrito (solo móvil) ── */}
      <button className="cart-fab" onClick={() => setCarritoAbierto(true)}>
        <span>🛒 Carrito ({totalItems})</span>
        <span>{fmt(subtotal)}</span>
      </button>

      {/* Fondo oscuro al abrir el carrito en móvil */}
      {carritoAbierto && (
        <div className="cart-overlay" onClick={() => setCarritoAbierto(false)}/>
      )}

      {/* ── Carrito deslizable (solo móvil) ── */}
      <div className={`cart-panel ${carritoAbierto ? 'open' : ''}`}>
        <button className="sheet-handle" onClick={() => setCarritoAbierto(false)} aria-label="Cerrar carrito">
          <span/>
        </button>

        <div className="cart-header">
          <span>Carrito ({totalItems} items)</span>
          {carrito.length > 0 && (
            <button onClick={limpiar} style={{background:'none',border:'none',cursor:'pointer',color:'var(--color-muted)',fontSize:12}}>
              Limpiar
            </button>
          )}
        </div>

        <div className="cart-items">
          {carrito.length === 0 && (
            <div style={{textAlign:'center',padding:'30px 16px',color:'var(--color-hint)',fontSize:13}}>
              Selecciona productos
            </div>
          )}
          {carrito.map(item => (
            <div key={item.id} className="cart-item">
              <div className="ci-name">{item.nombre}</div>
              {esKilo(item) ? (
                <button className="ci-peso" onClick={() => corregirCantidad(item)}>
                  {item.qty} kg
                </button>
              ) : (
                <div className="ci-qty">
                  <button onClick={() => cambiarQty(item.id, -1)}>−</button>
                  <span>{item.qty}</span>
                  <button onClick={() => cambiarQty(item.id, 1)}>+</button>
                </div>
              )}
              <div className="ci-total">{fmt(item.precio_bruto * item.qty)}</div>
            </div>
          ))}
        </div>

        {renderCobro()}
      </div>

      {/* ══ Modal: pesar / corregir cantidad ══ */}
      {editQty && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.4)',zIndex:2200,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}
          onClick={() => setEditQty(null)}>
          <div className="card" style={{width:'100%',maxWidth:340}} onClick={e => e.stopPropagation()}>
            <div className="section-title" style={{marginBottom:10}}>
              {editQty.esKilo ? '⚖ ' : ''}{editQty.nombre}
            </div>
            <div className="form-group">
              <label className="form-label">
                {editQty.esKilo ? 'Peso en kilos (ej: 1.225)' : 'Cantidad'}
              </label>
              <input className="form-input" type="text" autoFocus
                inputMode="decimal"
                value={editQty.valor}
                onChange={e => setEditQty({...editQty, valor: e.target.value.replace(/[^0-9.,]/g, '')})}
                onKeyDown={e => e.key === 'Enter' && confirmarCantidad()}
                onFocus={e => e.target.select()}
                placeholder={editQty.esKilo ? '0.000' : '0'}
                style={{fontSize:20,fontWeight:700,textAlign:'center',padding:'12px'}}/>
            </div>
            <div style={{display:'flex',gap:10}}>
              <button className="btn btn-primary" style={{flex:1}} onClick={confirmarCantidad}>Confirmar</button>
              <button className="btn btn-secondary" onClick={() => setEditQty(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* ══ Escáner con cámara ══ */}
      {escaneando && (
        <EscanerCodigo
          onDetectado={codigoDetectado}
          onCerrar={() => { setEscaneando(false); setScanResultado(null) }}
          resultado={scanResultado}
        />
      )}

      {/* ══ MODAL POST-VENTA — Boleta ══ */}
      {ventaCompletada && (
        <div style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,.6)',
          display:'flex', alignItems:'center', justifyContent:'center',
          zIndex:2000, padding:20
        }}>
          <div className="card" style={{
            width:'100%', maxWidth:420, maxHeight:'92vh',
            display:'flex', flexDirection:'column', padding:0, overflow:'hidden'
          }}>
            {/* Header modal */}
            <div style={{
              padding:'16px 20px', borderBottom:'1px solid var(--color-border)',
              display:'flex', justifyContent:'space-between', alignItems:'center'
            }}>
              <div>
                <div style={{fontWeight:700, fontSize:15}}>✅ Venta emitida</div>
                <div style={{fontSize:12, color:'var(--color-muted)'}}>
                  {emitidoNombre} #{emitidoNumero}
                </div>
              </div>
              <button onClick={cerrarModalVenta} style={{background:'none',border:'none',cursor:'pointer',fontSize:20,color:'var(--color-muted)'}}>✕</button>
            </div>

            {/* Preview de la boleta */}
            <div style={{flex:1, overflowY:'auto', padding:'16px', background:'#f0f0ee'}}>
              <div style={{background:'white', boxShadow:'0 2px 8px rgba(0,0,0,.1)', display:'inline-block', width:'100%'}}>
                <Boleta venta={ventaCompletada} usuario={usuario}/>
              </div>
            </div>

            {/* Acciones */}
            <div style={{padding:'16px 20px', borderTop:'1px solid var(--color-border)', display:'flex', flexDirection:'column', gap:8}}>
              <button className="btn btn-primary btn-block" onClick={imprimir}>
                🖨️ Imprimir {emitidoNombre.toLowerCase()}
              </button>
              <button className="btn btn-secondary btn-block" onClick={descargarPdf} disabled={generandoPdf}>
                {generandoPdf ? 'Generando PDF...' : '📥 Descargar PDF'}
              </button>
              <button className="btn btn-secondary btn-block" onClick={cerrarModalVenta} style={{color:'var(--color-muted)'}}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
